import { FC, useCallback, useContext, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { ProgressBar } from "primereact/progressbar";
import { AppContext } from "../../App";
import { getFileUrlFromId } from "../../helpers/getFileUrlFromId";
import Avatar, { AvatarSize } from "../Shared/Avatar/Avatar";
import Tooltip from "../UiLib/CustomTooltip/CustomTooltip";
import BoardFrame from "../../assets/board_frame.svg";
import ProjectFrame from "../../assets/project_frame.svg";
import SubprojectIcon from "../../assets/subproject.svg";
import ProjecFrameGray from "../../assets/project_frame_gray.svg";
import "./ProjectsMain.css"
import { ICardProject } from "../../common/api_interfaces/project/ICardProjectProject";

interface ProjectCardProps {
    project: ICardProject;
    hrefCoverCompany: string | undefined;
    viewProjects?: boolean;
}

const ProjectCard: FC<ProjectCardProps> = observer(({
    project,
    hrefCoverCompany,
    viewProjects
}: ProjectCardProps) => {
    const { showToast, archivedPage } = useContext(AppContext);
    const navigate = useNavigate();

    const handleClick = useCallback(() => {
        if (project.isUserInProject || viewProjects) {
            const path = archivedPage
                ? `/archive/project/${project.id}`
                : `/project/${project.id}`;
            navigate(path);
        } else {
            showToast("У вас нет прав на переход в проект!");
        }
    }, [project.id, project.isUserInProject, viewProjects, archivedPage, navigate, showToast]);

    const formatDate = useCallback((dateStr: string | number | Date): string => {
        try {
            const date = new Date(dateStr);
            const padWithZero = (value: number) => value.toString().padStart(2, '0');
            return `${padWithZero(date.getDate())}.${padWithZero(date.getMonth() + 1)}.${date.getFullYear()}`;
        } catch {
            return "-";
        }
    }, []);

    const datePeriod = useMemo(() => {
        return project.dateTo
            ? `${formatDate(project.dateFrom)}-${formatDate(project.dateTo)}`
            : formatDate(project.dateFrom);
    }, [project.dateFrom, project.dateTo, formatDate]);

    const uniqueUsers = useMemo(() => {
        return project.users?.filter((user, index, array) =>
            array.findIndex(u => u.id === user.id) === index
        ) || [];
    }, [project.users]);

    const displayedUsers = useMemo(() => {
        return uniqueUsers.slice(0, 4);
    }, [uniqueUsers]);

    const remainingUsersCount = useMemo(() => {
        return Math.max(0, uniqueUsers.length - 4);
    }, [uniqueUsers]);

    const shouldShowTooltip = useMemo(() => {
        return project.shortDescription && project.shortDescription.length > 50;
    }, [project.shortDescription]);

    const projectImageUrl = useMemo(() => {
        return project.img ? getFileUrlFromId(project.img) : hrefCoverCompany;
    }, [project.img, hrefCoverCompany]);

    return (
        <div
            onClick={handleClick}
            className="project-card-container"
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && handleClick()}
        >
            <div className="project-card-img-wrap">
                <div className="project-card-info-hov">
                    <div className="project-card-boards-popup">
                        <div className="project-card-boards-info">
                            <div className="project-card-boards-info-flex">
                                <div className="p_blue p_14 text_margin">
                                    {project.boardsNum}
                                </div>
                                <img src={BoardFrame} alt="Досок" />
                            </div>
                            <div className="project-card-boards-info-flex">
                                <div className="p_14 text_margin">
                                    {project.projectsNum}
                                </div>
                                <img src={ProjectFrame} alt="Проектов" />
                            </div>
                        </div>
                        <div className="project-card-dates-period">
                            {datePeriod}
                        </div>
                    </div>
                </div>
                <div className="project-card-img">
                    <img src={projectImageUrl} alt={project.shortDescription ?? ""} />
                </div>
            </div>

            <div className="card_projects_info">
                <div className="project-card-text-name-title-container">
                    <div className="project-card-text-name-container">
                        {project.isSubProject && (
                            <img
                                className="nav-panel_suproject-image"
                                src={SubprojectIcon}
                                alt="subproject-icon"
                            />
                        )}

                        <Tooltip
                            text={shouldShowTooltip ? project.shortDescription ?? "" : ""}
                            placement="bottom"
                            offset={5}
                        >
                            <h1 className="project-card-text-name">
                                {project.shortDescription}
                            </h1>
                        </Tooltip>
                    </div>
                </div>

                <div className="card_projects_code">
                    <div className="text_Card_code">{project.code}</div>
                </div>

                <div className="text_Card_description_container">
                    <p className="project-card-text-description">{project.description}</p>
                </div>

                <div className="card_projects_footer">
                    <div className="card_projects_users">
                        {displayedUsers.map((user) => (
                            <div key={user.id} className="avatar_group_item">
                                <Avatar
                                    userId={user.id}
                                    size={AvatarSize.s}
                                    userName={user.name}
                                />
                            </div>
                        ))}
                        {remainingUsersCount > 0 && (
                            <div className="count_users">
                                <div className="p_gray p_19">
                                    +{remainingUsersCount}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="card_projects_boards_info_footer">
                        <div className="project-card-boards-info-flex">
                            <div className="p_blue p_14 text_margin">{project.boardsNum}</div>
                            <img src={BoardFrame} alt="Досок" />
                        </div>
                        <div className="project-card-boards-info-flex">
                            <div className="p_14 text_margin p_gray">{project.projectsNum}</div>
                            <img src={ProjecFrameGray} alt="Проектов" />
                        </div>
                    </div>

                    <div className="card_projects_progress">
                        <div className='card_projects_progress_item'>
                            <p className='genInfo_body_item_p11 p_gray'>ПРОГРЕСС</p>
                            <p className='genInfo_body_item_p11 p_blue'>{project.percentageCompletion}%</p>
                        </div>
                        <ProgressBar value={project.percentageCompletion ?? 0} />
                    </div>
                </div>
            </div>
        </div>
    );
});

export default ProjectCard;